import { AssistantPlanEntriesButtons } from '../AssistantPlanEntriesButtons';
import {
  AssistantChatMessage,
  AssistantNavigationTarget,
  getLastAssistantMessageIndex,
  selectedCoreActionModalAtom,
  selectedDateAtom,
  settingsSidebarStateAtom,
  useAssistantChat,
  useAuthContext,
  walkthroughChatPrefillAtom,
} from '../../../data-access';
import {
  buildNearbyPlacesByEventId,
  buildRichContextEntriesByEventId,
  collectRichContextEntriesForSuggestedBlocks,
} from '../AssistantChatSuggestedBlocks/richContextNearbyPlaces';
import LifestackIcon from '../../../assets/logo/logo_transparent.png';
import './AssistantChatContent.scss';
import { FC, Fragment, ReactNode, useMemo, useState, useCallback, useEffect } from 'react';
import { Box, IconButton, Stack, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import type { SlashCommandShortcut } from '../AssistantChatInput';
import { AssistantChatInput } from '../AssistantChatInput';
import { AssistantChatSuggestedBlocks } from '../AssistantChatSuggestedBlocks';
import { useChatCTAHandler } from '../../../hooks/useChatCTAHandler';
import { ConversationQuestionCard } from '../ConversationQuestionCard';
import { AnimatedText, ChatMarkdown, TypingDotsIndicator } from '../../common';
import { scrollToCalendarTime } from '../../../helpers/calendar.helpers';
import { useFeatureFlag } from '../../../utils';
import {
  collectRichContextEntriesNearMessage,
  filterWorkoutScheduleBlocksWithRichContextCtas,
} from '../../../data-access/helpers/chat.helpers';
import dayjs from 'dayjs';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { AssistantChatCtaButtonsList } from '../AssistantChatCtaButtonsList';
import PlanIntroBubble from '../bubbles/PlanIntroBubble';
import type { PlanIntroConfirmState } from '../bubbles/PlanIntroBubble';
import PlanPreviewBubble from '../bubbles/PlanPreviewBubble';
import InsightSuggestionsBlock from '../bubbles/InsightSuggestionsBlock';
import { ArrowBack, ArrowDownward, Close } from '@mui/icons-material';
import { AnimatePresence, motion } from 'framer-motion';
import { useChatScrollManager, useAllPlanSuggestedModal } from '../../../hooks';
import { useStatusTextDisplay } from '../hooks/useStatusTextDisplay';
import { startCase } from 'lodash';
import {
  chatV2QuickActionCategories,
  QuickActionCategoryList,
  QuickActionList,
} from '../../Chat/ChatV2';
import { useQuickActionSelection } from '../hooks/useQuickActionSelection';
import clsx from 'clsx';
import { useNavigate } from 'react-router-dom';
import { Path } from '../../../routing';
import { AssistantChatHeader } from '../AssistantChatHeader';
import { AssistantChatRichContextCard } from '../AssistantChatRichContextCard';
import { AssistantChatCommitActions } from '../AssistantChatCommitActions';
import { AssistantChatNavigationButton } from '../AssistantChatNavigationButton';
import { NAVIGATION_TARGET_ACTIONS } from '../AssistantChatNavigationButton/navigationTargets';
import { trackEventMixpanel } from '../../../utils';
import { useStreamingContent } from '../../../data-access/context/AssistantChat/streamingStore';
import { useTranslation } from 'react-i18next';

interface AssistantChatContentProps {
  processedIds: string[];
  setProcessedIds: (ids: string[]) => void;
  onClear: () => void;
  containerHeight?: number;
  hideHeader?: boolean;
  quickActionsPlacement?: 'start-message' | 'above-input';
  className?: string;
}

/** v4 only: list/table/fence markdown needs a defined bubble width or list rows collapse.
 * Mirrors lifestack_calendar AssistantChatDesc.shouldFillCompactMarkdownWidth. */
const shouldFillCompactMarkdownWidth = (text: string): boolean =>
  /(?:^|\n)\s*(?:[-*+]|\d+\.)\s/.test(text) ||
  text.includes('```') ||
  /(?:^|\n)\s*\|.+\|/.test(text);

interface AiChatBubbleProps {
  /** Explicit width chrome — 'fill' = width 94%; 'suggested-blocks' = transparent + 94%;
   * 'rich-context' = width 94% + F2EFF6 tint (query-only rich-context bubbles). */
  modifier?: 'suggested-blocks' | 'fill' | 'rich-context';
  className?: string;
  /**
   * When provided, recompute fill from streaming + settled markdown so list/table/fence
   * replies pick up width: 94% mid-stream (message.content stays '' until finalize).
   */
  markdownFill?: {
    messageId: string;
    content: string;
    isStreaming: boolean;
  };
  children: ReactNode;
}

const AiChatBubble: FC<AiChatBubbleProps> = ({ modifier, markdownFill, className, children }) => {
  const streamingContent = useStreamingContent(
    markdownFill?.messageId,
    markdownFill?.isStreaming
  );
  const fillFromMarkdown =
    !!markdownFill &&
    shouldFillCompactMarkdownWidth(streamingContent ?? markdownFill.content ?? '');
  const fill = modifier === 'fill' || fillFromMarkdown;

  return (
    <div
      className={clsx(
        'assistant-chat-content-container__bubble',
        className,
        {
          'assistant-chat-content-container__bubble--suggested-blocks':
            modifier === 'suggested-blocks',
          'assistant-chat-content-container__bubble--rich-context': modifier === 'rich-context',
          'assistant-chat-content-container__bubble--fill':
            modifier !== 'suggested-blocks' && modifier !== 'rich-context' && fill,
        }
      )}
    >
      <div className="assistant-chat-content-container__bubble-content">{children}</div>
    </div>
  );
};

interface AssistantMessageBodyProps {
  item: AssistantChatMessage;
  index: number;
  messages: AssistantChatMessage[];
  isStreaming: boolean;
}

const AssistantMessageBody: FC<AssistantMessageBodyProps> = ({
  item,
  index,
  messages,
  isStreaming,
}) => {
  const streamingContent = useStreamingContent(item.messageId, isStreaming);
  const displayContent = streamingContent ?? item.content;
  const { featureFlags } = useFeatureFlag();
  const isV4Enabled = featureFlags.FF_lifestack_v4 ?? true;

  return (
    <>
      {displayContent && (
        <div className="assistant-chat-content-container__bubble__description">
          <ChatMarkdown
            content={displayContent}
            className="assistant-chat-content-container__bubble__description__markdown"
          />
          {/* v3 only: purple streaming cursor. v4 shows status text under the
              bubble instead (see sequence-status row). */}
          {!isV4Enabled &&
            isStreaming &&
            getLastAssistantMessageIndex(messages) === index &&
            displayContent && (
              <div className="assistant-chat-content-container__bubble__description__streaming-indicator">
                <div className="assistant-chat-content-container__bubble__description__streaming-dot" />
              </div>
            )}
        </div>
      )}
    </>
  );
};

const AssistantChatContent: FC<AssistantChatContentProps> = ({
  processedIds,
  setProcessedIds,
  onClear,
  containerHeight,
  hideHeader = false,
  quickActionsPlacement = 'start-message',
  className,
}) => {
  const { t } = useTranslation();
  const { featureFlags } = useFeatureFlag();
  const isV4Enabled = featureFlags.FF_lifestack_v4 ?? true;
  const {
    messages,
    handlePlanEntry,
    loading,
    loadingStatusText,
    isStreaming,
    targetEventOnCalendar,
    currentSessionType,
    handleFeelingSelection,
    handleActivitySelection,
    sendRecommendation,
    removeTemporalSuggestedEvent,
    setTemporalSuggestedEvents,
    startGeneralChat,
    setOpenAssistantChat,
    sendQuestionAnswer,
    startPlanMyDayIntro,
    sendMessage,
    injectDebugMockConversation,
    injectDebugMockReplanConversation,
    injectDebugMockPlanStatusConversation,
  } = useAssistantChat();
  const { handleCTAEvent, handleCTATask, loadingEventId } = useChatCTAHandler({
    removeTemporalSuggestedEvent,
    setTemporalSuggestedEvents,
  });
  const setSettingsSidebarState = useSetRecoilState(settingsSidebarStateAtom);
  const setSelectedCoreActionModal = useSetRecoilState(selectedCoreActionModalAtom);
  const navigate = useNavigate();
  const setSelectedDate = useSetRecoilState(selectedDateAtom);
  const selectedDate = useRecoilValue(selectedDateAtom);
  const [focusCounter, setFocusCounter] = useState(0);
  const { user } = useAuthContext();
  const firstName = useMemo(() => startCase(user?.name?.split(' ')[0]), [user?.name]);
  const [quickActionPrefill, setQuickActionPrefill] = useState<{
    text: string;
    version: number;
  } | null>(null);
  const walkthroughChatPrefill = useRecoilValue(walkthroughChatPrefillAtom);
  const activePrefill = walkthroughChatPrefill ?? quickActionPrefill;
  const [clearVersion, setClearVersion] = useState(0);
  const [planIntroConfirm, setPlanIntroConfirm] = useState<PlanIntroConfirmState | null>(null);
  const handlePlanIntroConfirmStateChange = useCallback((state: PlanIntroConfirmState | null) => {
    setPlanIntroConfirm(state);
  }, []);
  const isPlanIntroActive = useMemo(
    () => messages.some((msg) => msg.planIntro) && !messages.some((msg) => msg.allPlanPreview),
    [messages]
  );
  const { isLoading: isAllPlanLoading, statusLines: allPlanStatusLines } =
    useAllPlanSuggestedModal();
  const isPlanDayFetching =
    isAllPlanLoading || allPlanStatusLines.length > 0 || !!planIntroConfirm?.isConfirming;

  const {
    selectedCategory,
    expandedItemId,
    handleCategoryPress,
    handleItemPress,
    handleSubItemPress,
    resetSelection,
  } = useQuickActionSelection({
    onPlanEntry: handlePlanEntry,
    onStartGeneralChat: startGeneralChat,
    onPlanMyEntireDay: startPlanMyDayIntro,
    onPrefill: (text: string) => {
      setQuickActionPrefill({ text, version: Date.now() });
      setFocusCounter((prev) => prev + 1);
    },
    onDebugMockAction: (id: string) => {
      if (id === 'debug-mock-rich-context') {
        injectDebugMockConversation();
      } else if (id === 'debug-mock-replan') {
        injectDebugMockReplanConversation();
      } else if (id === 'debug-mock-plan-status') {
        injectDebugMockPlanStatusConversation();
      }
    },
  });

  // Manage status text display with minimum duration
  const currentStatusText = useStatusTextDisplay(loadingStatusText);
  const loadingText = useMemo(() => {
    if (!currentStatusText) return '';
    const trimmed = currentStatusText.trim();
    return trimmed.endsWith('...') ? trimmed.slice(0, -3).trimEnd() : trimmed;
  }, [currentStatusText]);
  const lastAssistantMessage = messages[getLastAssistantMessageIndex(messages)];
  const lastMessageHasLiveSequenceStatus = Boolean(lastAssistantMessage?.statusText);
  // Messages after the latest user turn. Sequence status on an earlier turn
  // must not hide the indicator for the request that's in flight now.
  let lastUserIndex = -1;
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    if (messages[i].role === 'user') {
      lastUserIndex = i;
      break;
    }
  }
  const currentTurnMessages = lastUserIndex >= 0 ? messages.slice(lastUserIndex + 1) : messages;
  const latestTurnAssistant = [...currentTurnMessages]
    .reverse()
    .find((message) => message.role === 'assistant' && !message.start);
  const latestTurnStreamingContent = useStreamingContent(
    latestTurnAssistant?.messageId,
    isStreaming
  );
  const currentTurnHasStatusText = currentTurnMessages.some(
    (message) => message.role === 'assistant' && !!message.statusText
  );
  const currentTurnHasPlanLoader = currentTurnMessages.some(
    (message) => !!message.allPlanPreview || !!message.planIntro
  );
  // v4: sequence.status pulses under the last bubble — don't duplicate it
  // with the classic footer ticker. When the stream is still open and that
  // line hasn't arrived, a hug-content typing bubble stands in between
  // sequence messages.
  const showFooterStatus =
    (loading || (isStreaming && !!currentStatusText)) &&
    !lastMessageHasLiveSequenceStatus &&
    (!isV4Enabled || !!loadingText);
  const showDotsOnly =
    !isV4Enabled && loading && !currentStatusText && !loadingStatusText?.length;
  const showSequenceDotsBubble =
    isV4Enabled &&
    (loading || isStreaming) &&
    !currentTurnHasStatusText &&
    !currentStatusText &&
    !loadingStatusText?.length &&
    latestTurnStreamingContent === null &&
    !currentTurnHasPlanLoader &&
    !isPlanDayFetching;

  const { messagesContainerRef, messagesEndRef, isOverflowing, isAtBottom, scrollToBottom } =
    useChatScrollManager({
      messages,
      containerHeight,
      keepToBottom: currentSessionType === 'recommendation',
      isStreaming,
      statusText: loadingStatusText,
    });

  useEffect(() => {
    if (!showSequenceDotsBubble) return;
    const timeout = window.setTimeout(() => scrollToBottom(), 50);
    return () => window.clearTimeout(timeout);
  }, [showSequenceDotsBubble, scrollToBottom]);

  const onCheckCalendar = (startTime: string) => {
    const date = targetEventOnCalendar?.date;
    const time = dayjs(startTime).format('HH:mm');
    if (!date) return;
    setSelectedDate(dayjs(date));
    scrollToCalendarTime({ date, time });
  };

  const handleCtaButtonPress = useCallback(
    (buttonId: string) => {
      if (currentSessionType === 'recommendation') {
        if (buttonId.startsWith('feeling_')) {
          const feeling = buttonId.replace('feeling_', '');
          const isCustom = feeling === 'custom';
          if (isCustom) {
            setFocusCounter((prev) => prev + 1);
          }
          return handleFeelingSelection(feeling, isCustom);
        } else if (buttonId.startsWith('activity_')) {
          const activity = buttonId.replace('activity_', '');
          const isCustom = activity === 'custom';
          if (isCustom) {
            setFocusCounter((prev) => prev + 1);
          }
          return handleActivitySelection(activity, isCustom);
        } else if (buttonId.startsWith('final_')) {
          const action = buttonId.replace('final_', '');

          if (action === 'send') {
            return sendRecommendation();
          } else if (action === 'custom') {
            setFocusCounter((prev) => prev + 1);
            return Promise.resolve();
          }
        }
      }
    },
    [currentSessionType, handleFeelingSelection, handleActivitySelection, sendRecommendation]
  );

  const hasSubsequentAssistantMessages = useCallback(
    (currentIndex: number) => {
      return messages.slice(currentIndex + 1).some((msg) => msg.role === 'assistant');
    },
    [messages]
  );

  const renderQuickActionTitle = (title: string) => {
    const parts = title.trim().split(/\s+/);
    const first = parts[0] ?? '';
    const rest = parts.slice(1).join(' ');
    if (!rest) {
      return (
        <span className="assistant-chat-content-container__quick-actions-option__label__primary">
          {first}
        </span>
      );
    }
    return (
      <>
        <span className="assistant-chat-content-container__quick-actions-option__label__primary">
          {first}
        </span>{' '}
        <span className="assistant-chat-content-container__quick-actions-option__label__secondary">
          {rest}
        </span>
      </>
    );
  };

  const renderQuickActions = () => {
    if (!selectedCategory) {
      if (quickActionsPlacement === 'above-input') {
        return (
          <>
            <div className="assistant-chat-content-container__quick-actions-vertical">
              {chatV2QuickActionCategories.map((category, index) => (
                <motion.button
                  key={category.id}
                  type="button"
                  className="assistant-chat-content-container__quick-actions-option"
                  onClick={() => handleCategoryPress(category)}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2, delay: index * 0.04, ease: 'easeOut' }}
                >
                  <span
                    className="assistant-chat-content-container__quick-actions-option__icon"
                    style={{ backgroundColor: category.accentColor }}
                  >
                    {category.icon}
                  </span>
                  <span className="assistant-chat-content-container__quick-actions-option__label">
                    {renderQuickActionTitle(
                      t(`task.ai.quickActions.categories.${category.id}.title`, {
                        defaultValue: category.title,
                      })
                    )}
                  </span>
                </motion.button>
              ))}
            </div>
          </>
        );
      }

      return (
        <QuickActionList
          categories={chatV2QuickActionCategories}
          onCategoryPress={handleCategoryPress}
        />
      );
    }

    return (
      <Stack
        gap={1.5}
        className="assistant-chat-content-container__quick-actions-category"
        width="100%"
      >
        {quickActionsPlacement === 'above-input' && (
          <button
            type="button"
            className="assistant-chat-content-container__quick-actions-back"
            onClick={resetSelection}
            aria-label={t('task.ai.quickActions.closeSubCategoryOptions')}
          >
            <Close className="assistant-chat-content-container__quick-actions-back__icon" />
          </button>
        )}
        <QuickActionCategoryList
          category={selectedCategory}
          onItemPress={handleItemPress}
          onSubItemPress={handleSubItemPress}
          expandedItemId={expandedItemId}
        />
      </Stack>
    );
  };

  const renderItem = ({ item, index }: { item: AssistantChatMessage; index: number }) => {
    if (item.start && messages.length > 1) {
      return null;
    }

    if (item.cancelled) {
      return (
        <div key={item.messageId} className="assistant-chat-content-container__cancelled">
          {t('task.ai.input.cancelled')}
        </div>
      );
    }

    const isUser = item.role === 'user';
    if (isUser) {
      return (
        <div className="assistant-chat-content-container__bubble assistant-chat-content-container__bubble--user">
          <Typography className="assistant-chat-content-container__bubble__title" variant="body1">
            {item.content}
          </Typography>
        </div>
      );
    }
    // Only handle the start message condition
    if (item.start) {
      if (quickActionsPlacement === 'above-input') {
        return (
          <Stack
            alignItems="center"
            key={item.messageId}
            className="assistant-chat-content-container__item-start"
            gap={1.5}
          >
            <div className="assistant-chat-content-container__item-start__icon">
              <img src={LifestackIcon} alt="Lifestack" width={18} height={18} />
            </div>
            <Typography
              variant="h3"
              textAlign="center"
              className="assistant-chat-content-container__title"
            >
              {t('task.ai.start.hi')}{' '}
              <Typography
                component="span"
                variant="h3"
                className="assistant-chat-content-container__title__name"
              >
                {firstName} 👋
              </Typography>{' '}
            </Typography>
            <Typography
              variant="body1"
              textAlign="center"
              className="assistant-chat-content-container__subtitle"
            >
              {t('task.ai.start.subtitle')}
            </Typography>
          </Stack>
        );
      }
      return (
        <Stack
          alignItems="center"
          key={item.messageId}
          className="assistant-chat-content-container__item-start"
          gap={2}
        >
          {!selectedCategory && (
            <>
              {/* AI Icon */}

              <div className="assistant-chat-content-container__item-start__icon">
                <img src={LifestackIcon} alt="Lifestack" width={18} height={18} />
              </div>

              {/* Title - "How can I help?" */}
              {item.title && (
                <Typography
                  variant="h3"
                  textAlign="center"
                  className="assistant-chat-content-container__title"
                >
                  {t('task.ai.start.hey')}{' '}
                  <Typography
                    component="span"
                    variant="h3"
                    className="assistant-chat-content-container__title__name"
                  >
                    {firstName}! {t('task.ai.start.title')}
                  </Typography>
                </Typography>
              )}
            </>
          )}
          {quickActionsPlacement === 'start-message' ? renderQuickActions() : null}
        </Stack>
      );
    }
    // v4 `navigate` event: a tappable "open this screen" button. Deliberately
    // not wrapped in AiChatBubble — it is an action under the reply text, not a
    // reply of its own, so it gets no chat-bubble background (same as RN).
    if (item.navigation) {
      return (
        <div key={item.messageId} className="assistant-chat-content-container__ai-row">
          <AssistantChatNavigationButton item={item} onNavigate={handleNavigateTarget} />
        </div>
      );
    }

    // Question card
    if (item.question) {
      return (
        <div key={item.messageId} className="assistant-chat-content-container__ai-row">
          <AiChatBubble modifier="fill">
            <ConversationQuestionCard
              question={item.question}
              onSelect={(option) => sendQuestionAnswer(item.messageId, option)}
              disabled={item.isAnswered || loading || isStreaming}
            />
          </AiChatBubble>
        </div>
      );
    }

    // Plan My Day intro card — "what would you like to plan today?"
    // RN PlanIntroBubble always uses compactMenuTriggerFill (width 94%).
    if (item.planIntro) {
      return (
        <div key={item.messageId} className="assistant-chat-content-container__ai-row">
          <AiChatBubble modifier="fill">
            <PlanIntroBubble onConfirmStateChange={handlePlanIntroConfirmStateChange} />
          </AiChatBubble>
        </div>
      );
    }

    // Live Plan My Day/replan results card.
    // Loading status is bare AnimatedText (no bubble chrome); results wrap
    // themselves in fill bubble chrome inside PlanPreviewBubble.
    if (item.allPlanPreview) {
      return (
        <div key={item.messageId} className="assistant-chat-content-container__ai-row">
          <PlanPreviewBubble />
        </div>
      );
    }

    const relatedRichContextEntries = collectRichContextEntriesNearMessage(messages, index);
    const visibleSuggestedBlocks = filterWorkoutScheduleBlocksWithRichContextCtas(
      item.suggestedBlocks ?? [],
      relatedRichContextEntries
    );
    const queryRichContextEntries = item.richContexts?.filter((e) => e.source === 'query') ?? [];
    const adjustmentRichContextEntries =
      item.richContexts?.filter((e) => e.source === 'adjustment') ?? [];
    const hasTrailingContent =
      !!item.insightSuggestions?.length || !!item.ctaButtons?.length || !!item.commitActions?.length;

    // Each kind of content (text, one bubble per query rich-context entry,
    // suggested blocks, the trailing CTA/insight row) renders as its own
    // bubble, in the same relative order the single combined bubble used to
    // render them in. 'adjustment'-sourced rich-context entries don't get a
    // bubble at all — they're matched to the suggested block they belong to
    // (see buildRichContextEntriesByEventId) and render inside that block's
    // own card instead. Rich-context cards use the plain ECEAF3 bubble
    // background like any other AI turn — no separate tint, and no nested
    // card chrome of their own (see the `.rc-card`/`.rc-content-card`
    // override in AssistantChatContent.scss).
    //
    // Width modifiers mirror RN bubbleStyles:
    // - default AI bubble: maxWidth 94%, hugs content
    // - 'fill': width 94% (rich context / list|table|fence markdown / trailing CTAs)
    // - 'suggested-blocks': transparent chrome + width 94%
    const isStreamingThisItem =
      isStreaming && getLastAssistantMessageIndex(messages) === index;
    // Token streams keep content: '' and render from streamingStore, so they
    // still need a text bubble. A finished sequence message must not grow an
    // empty bubble just because the stream is still open — the typing dots
    // bubble covers that gap when there is no status text.
    const isLiveTokenStream =
      isStreamingThisItem &&
      latestTurnStreamingContent !== null &&
      item.messageId === latestTurnAssistant?.messageId;
    const sections: {
      key: string;
      modifier?: 'suggested-blocks' | 'fill' | 'rich-context';
      markdownFill?: boolean;
      content: ReactNode;
    }[] = [];

    // Live token streams keep content: '' and render from streamingStore — still
    // need a text bubble so list markdown can pick up --fill mid-stream.
    // v4 sequence.status lives under the bubble (not inside it) — see the
    // sequence-status row after sections, matching RN AssistantMessageItem.
    if (item.title || item.content || isLiveTokenStream) {
      sections.push({
        key: 'text',
        markdownFill: true,
        content: (
          <>
            {item.title && (
              <Typography
                variant="body2"
                className="assistant-chat-content-container__bubble__title"
              >
                {item.title}
              </Typography>
            )}
            {(item.content || isLiveTokenStream) && (
              <AssistantMessageBody
                item={item}
                index={index}
                messages={messages}
                isStreaming={isStreaming}
              />
            )}
          </>
        ),
      });
    }

    // Multiple query rich-context entries each get their own bubble rather
    // than stacking inside one, matching how separate assistant turns read.
    // RN ActionResultBubble always uses compactMenuTriggerFill.
    queryRichContextEntries.forEach((entry, entryIndex) => {
      sections.push({
        key: `query-rich-context-${entryIndex}`,
        modifier: 'rich-context',
        content: <AssistantChatRichContextCard entries={[entry]} />,
      });
    });

    if (visibleSuggestedBlocks.length) {
      sections.push({
        key: 'suggested-blocks',
        modifier: 'suggested-blocks',
        content: (
          <AssistantChatSuggestedBlocks
            blocks={visibleSuggestedBlocks}
            loadingEventId={loadingEventId}
            processedIds={processedIds}
            onProcessedIdsChange={setProcessedIds}
            onManageCalendar={handleCTAEvent}
            onManageTodo={handleCTATask}
            onCheckCalendar={onCheckCalendar}
            // Match RN TextBubble/ActionResultBubble — v4 suggested cards
            // (Event/Task/Routine) always render via the "screen" variant
            // (SuggestionGroupCard + collapsible header), including
            // result_json / adjustments.schedule mocks.
            variant="screen"
            onDiscardBlock={removeTemporalSuggestedEvent}
            nearbyPlacesByEventId={buildNearbyPlacesByEventId(
              visibleSuggestedBlocks,
              collectRichContextEntriesForSuggestedBlocks(messages, index)
            )}
            richContextEntriesByEventId={buildRichContextEntriesByEventId(
              visibleSuggestedBlocks,
              adjustmentRichContextEntries
            )}
          />
        ),
      });
    }

    if (hasTrailingContent) {
      // Insights/CTA lists are full-width controls (RN SuggestionBubble /
      // ActionResultBubble use compactMenuTriggerFill). Commit-only rows also
      // fill so width:100% children don't collapse inside a hug bubble.
      sections.push({
        key: 'trailing',
        modifier: 'fill',
        content: (
          <>
            {!!item.insightSuggestions?.length && (
              <InsightSuggestionsBlock
                insightSuggestions={item.insightSuggestions}
                onSendPrompt={(text) => sendMessage(text)}
              />
            )}
            {!!item.ctaButtons?.length && (
              <AssistantChatCtaButtonsList
                buttons={item.ctaButtons}
                onButtonPress={(buttonId) => {
                  handleCtaButtonPress(buttonId);
                }}
                disabled={loading || isStreaming || hasSubsequentAssistantMessages(index)}
              />
            )}
            {item.commitActions?.length && (
              <AssistantChatCommitActions
                actions={item.commitActions}
                onOpenSettings={handleOpenSettings}
              />
            )}
          </>
        ),
      });
    }

    if (!sections.length && !item.statusText) {
      return null;
    }

    return (
      <Fragment key={item.messageId}>
        {sections.map((section) => (
          <div
            key={`${item.messageId}-${section.key}`}
            className="assistant-chat-content-container__ai-row"
          >
            <AiChatBubble
              modifier={section.modifier}
              markdownFill={
                section.markdownFill
                  ? {
                      messageId: item.messageId,
                      content: item.content ?? '',
                      isStreaming: isStreamingThisItem,
                    }
                  : undefined
              }
            >
              {section.content}
            </AiChatBubble>
          </div>
        ))}
        {/* v4: sequence.status sits under the bubble with no card chrome —
            pulsing animated text (RN SequenceStatusText), not a purple dot. */}
        {!!item.statusText && (
          <div
            className="assistant-chat-content-container__sequence-status"
            role="status"
            aria-live="polite"
          >
            <AnimatedText
              text={item.statusText}
              className="assistant-chat-content-container__sequence-status__text"
            />
          </div>
        )}
      </Fragment>
    );
  };

  const memoizedPlaceholder = useMemo(() => {
    const lastUserMessage = messages.filter((msg) => msg.role === 'user').pop();
    if (!lastUserMessage) {
      return t('task.ai.input.askPlanDay');
    }
    return lastUserMessage.content;
  }, [messages, t]);

  const isStartOnlyScreen = useMemo(
    () => messages.length === 1 && messages[0]?.role === 'assistant' && !!messages[0]?.start,
    [messages]
  );

  const renderChatInput = () => {
    return (
      <Stack
        justifyContent={'flex-end'}
        alignItems={'flex-end'}
        className="assistant-chat-content-container__chat-input"
      >
        {quickActionsPlacement === 'above-input' && isStartOnlyScreen && (
          <motion.div
            className={clsx('assistant-chat-content-container__quick-actions-scrollable', {
              'assistant-chat-content-container__quick-actions-scrollable--sub-category':
                !!selectedCategory,
            })}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
          >
            {renderQuickActions()}
          </motion.div>
        )}
        <AnimatePresence>
          {isOverflowing && !isAtBottom && (
            <IconButton
              component={motion.button}
              onClick={scrollToBottom}
              className="assistant-chat-content-container__chat-input__arrow-down-button"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <ArrowDownward className="assistant-chat-content-container__chat-input__arrow-down-button__icon" />
            </IconButton>
          )}
        </AnimatePresence>
        {isPlanIntroActive ? (
          <div className="assistant-chat-content-container__plan-intro-actions">
            <IconButton
              className="assistant-chat-content-container__plan-intro-actions__back"
              onClick={onClearPress}
              aria-label={t('task.common.newChat')}
            >
              <ArrowBack fontSize="small" />
            </IconButton>
            <LoadingButton
              variant="contained"
              className="assistant-chat-content-container__plan-intro-actions__run"
              onClick={() => planIntroConfirm?.confirm()}
              disabled={!planIntroConfirm?.canConfirm || loading || isStreaming}
              loading={!!planIntroConfirm?.isConfirming}
            >
              {t('task.scheduler.run')}
            </LoadingButton>
          </div>
        ) : (
          <AssistantChatInput
            disabled={loading || isStreaming || isPlanDayFetching}
            placeholder={memoizedPlaceholder}
            focusCounter={focusCounter}
            setFocusCounter={setFocusCounter}
            prefillText={activePrefill?.text}
            prefillVersion={activePrefill?.version}
            clearVersion={clearVersion}
            hideLeadingIcon={quickActionsPlacement === 'above-input'}
            onShortcutSelect={handleSlashCommand}
          />
        )}
      </Stack>
    );
  };

  const handleSlashCommand = useCallback(
    (shortcut: SlashCommandShortcut) => {
      if (shortcut.command === '/plan-my-day') {
        startPlanMyDayIntro();
        return;
      }
      if (shortcut.planType) {
        handlePlanEntry(shortcut.planType, selectedDate.format('YYYY-MM-DD'), shortcut.prompt);
        return;
      }
      if (shortcut.prompt) {
        startGeneralChat(shortcut.prompt);
      }
    },
    [handlePlanEntry, startPlanMyDayIntro, startGeneralChat, selectedDate]
  );

  const onClearPress = () => {
    setProcessedIds([]);
    resetSelection();
    setQuickActionPrefill(null);
    setClearVersion((prev) => prev + 1);
    onClear();
  };

  const canGoBack = selectedCategory !== null || messages.length > 1;

  const handleBackPress = () => {
    if (selectedCategory) {
      resetSelection();
      return;
    }

    if (messages.length > 1) {
      onClearPress();
    }
  };

  const handleOpenSettings = () => {
    trackEventMixpanel('connect_tool', { source: 'assistant_chat' });
    setSettingsSidebarState({
      selectedParentKey: 'aiAssistant',
      expandedParentKeys: [],
    });
    navigate(`${Path.HOME}/settings`);
    setOpenAssistantChat(false);
  };

  // v4 `navigate` event: close the chat, then open the target settings section or modal.
  const handleNavigateTarget = (target: AssistantNavigationTarget) => {
    const action = NAVIGATION_TARGET_ACTIONS[target];
    if (!action) {
      return;
    }
    trackEventMixpanel('assistant_navigate', { source: 'assistant_chat', target });
    if (action.kind === 'settings') {
      setSettingsSidebarState({
        selectedParentKey: action.parentKey,
        expandedParentKeys: [],
      });
      navigate(`${Path.HOME}/settings${action.hash ? `#${action.hash}` : ''}`);
    } else {
      navigate(Path.HOME);
      setSelectedCoreActionModal(action.modal);
    }
    setOpenAssistantChat(false);
  };

  return (
    <div className={clsx('assistant-chat-content-container', className)}>
      {!hideHeader && (
        <AssistantChatHeader
          className="assistant-chat-content-container__header"
          showBackButton={canGoBack}
          onBackPress={handleBackPress}
          onOpenSettings={handleOpenSettings}
          onRefresh={onClearPress}
        />
      )}
      <Box
        p={2}
        className="assistant-chat-content-container__messages"
        style={{
          maxHeight: containerHeight,
        }}
        ref={messagesContainerRef}
      >
        {messages.map((item, index) => renderItem({ item, index }))}
        {showSequenceDotsBubble && (
          <div className="assistant-chat-content-container__ai-row">
            <AiChatBubble className="assistant-chat-content-container__bubble--typing">
              <TypingDotsIndicator aria-label={t('settings.common.loading')} />
            </AiChatBubble>
          </div>
        )}
        {(showFooterStatus || (showDotsOnly && !lastMessageHasLiveSequenceStatus)) && (
          <>
            {!showDotsOnly && (
              <motion.div
                key={currentStatusText}
                className="assistant-chat-content-container__loading"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35, ease: 'easeOut' }}
                role="status"
                aria-live="polite"
              >
                <AnimatedText
                  text={loadingText}
                  className="assistant-chat-content-container__loading__text"
                />
              </motion.div>
            )}
            {showDotsOnly && (
              <div
                className="assistant-chat-content-container__loading assistant-chat-content-container__loading--dots-only"
                role="status"
                aria-live="polite"
              >
                <div className="assistant-chat-content-container__bubble__description__streaming-dot" />
              </div>
            )}
          </>
        )}
        <div ref={messagesEndRef} />
      </Box>
      {renderChatInput()}
    </div>
  );
};

export default AssistantChatContent;
